import React, { useState } from 'react';
import SEO from '../components/SEO';
import PageHeader from '../components/PageHeader';
import Gallery from '../components/Gallery';
import Lightbox from '../components/Lightbox';
import { imageUrl } from '../lib/images';

export default function Referencer() {
  const [containerLightboxIndex, setContainerLightboxIndex] = useState(null);
  const containerImages = [
    'https://assets.auxodev.dk/assets/2e6d0d1c-821d-400c-80aa-275c6c0c86cc/fragt-af-40-ft-hc-1.jpg',
    'https://assets.auxodev.dk/assets/2e6d0d1c-821d-400c-80aa-275c6c0c86cc/40-ft-hc-losning-med-kran.jpg'
  ];

  const [specialLightboxIndex, setSpecialLightboxIndex] = useState(null);
  const specialImages = [
    'https://assets.auxodev.dk/assets/2e6d0d1c-821d-400c-80aa-275c6c0c86cc/fragt-af-dinosaurus-p-nedbygget-trailer.jpg'
  ];

  return (
    <>
      <SEO
        title="Fragtopgaver vi har løst | Fastlane ApS"
        description="Industrigods. Langt største delen af de fragtopgaver vi gennefører er industrigods af enhver art som køres fra firma til firma. Se billeder og cases af løste opgaver."
        canonicalUrl="https://fastlane.dk/godstransport-referencer"
      />

      <PageHeader
        title="Fragtopgaver vi har løst"
        subtitle="Billeder og eksempler på transporter vi har gennemført – fra standard industrigods til specialtransport."
        breadcrumbs={[
          { label: 'Ydelser', path: '/godstransport-i-europa' },
          { label: 'Fragtopgaver - Referencer' }
        ]}
      />

      <div className="py-12 sm:py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
          {/* Section 1: Industrigods (CMS Gallery) */}
          <section className="space-y-4">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#0f2b48]">Industrigods</h2>
            <p className="text-base text-slate-700 leading-relaxed">
              Langt største delen af de fragtopgaver vi gennefører er industrigods af enhver art som køres fra firma til firma.
            </p>
            {/* Dynamic CMS Gallery */}
            <Gallery slug="industrigods" columns={3} />
          </section>

          {/* Section 2: Hvordan man emballerer en lille maskine på palle (CMS Gallery) */}
          <section className="space-y-4 border-t border-slate-200 pt-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#0f2b48]">
              Hvordan man emballerer en lille maskine på palle
            </h2>
            <p className="text-base text-slate-700 leading-relaxed">
              Maskinen sendes på 4 euro paller der er sammenbygget, hvilket giver et areal på 240 x 160 cm. Billedserien er tilsendt af afsender - Konesilta, Græsslåmaskinen er godt beskyttet og kan håndteres med truck ved af-, på- og omlæsning.
            </p>
            {/* Dynamic CMS Gallery */}
            <Gallery slug="emballering-maskine" columns={3} />
          </section>

          {/* Section 3: Vejtransport af 40 ft high cube container */}
          <section className="space-y-4 border-t border-slate-200 pt-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#0f2b48]">
              Vejtransport af 40 ft high cube container
            </h2>
            <p className="text-base text-slate-700 leading-relaxed">
              For montagejob i Sverige har vi netop arrangeret fragt af en 40 ft high cube container kørt på vej fra Bramming (DK) til Katrineholm (SE) for losning med kran.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-2">
              {containerImages.map((img, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => setContainerLightboxIndex(idx)}
                  className="group relative aspect-4/3 overflow-hidden rounded-xl bg-slate-100 border border-slate-200 shadow-2xs hover:shadow-md transition-all text-left focus:outline-hidden cursor-pointer"
                  aria-label={`Vis 40 ft container billede ${idx + 1}`}
                >
                  <img
                    src={imageUrl(img, 'width=700,quality=85,format=auto')}
                    alt={`40 ft container transport ${idx + 1}`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                    <span className="opacity-0 group-hover:opacity-100 transition-opacity p-2.5 rounded-full bg-white/90 text-slate-900 shadow-md">
                      <i className="fa-solid fa-expand text-sm" aria-hidden="true" />
                    </span>
                  </div>
                </button>
              ))}
            </div>
            {containerLightboxIndex !== null && (
              <Lightbox
                images={containerImages}
                currentIndex={containerLightboxIndex}
                onClose={() => setContainerLightboxIndex(null)}
                onNavigate={(newIdx) => setContainerLightboxIndex(newIdx)}
              />
            )}
          </section>

          {/* Section 4: Dinosaur Model til Zoologisk Museum i Zürich (CMS Gallery) */}
          <section className="space-y-4 border-t border-slate-200 pt-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#0f2b48]">
              Dinosaur Model til Zoologisk Museum i Zürich
            </h2>
            <p className="text-base text-slate-700 leading-relaxed">
              2020 havde vi fornøjelsen at transportere en dinosaur model med tilbehør til Zoologisk Museum i Zürich. Jobbet blev udført på vegne af 10 Tons, der har fremstillet model og tilbehør og i tæt samarbejde med 10 Tons, Zoologisk Museum i Zürich og den udførende vognmand. Herunder dinosaur model ankommet i almindelig P-trailer, losses med maskine og køres frem til indgangsdør. Modellen der er pakket på transportvogn af 10 Tons, således at den netop kan komme gennem døren og køres / bæres det sidste stykke. Til sidst et billede af den færdige installation.
            </p>
            <p className="text-xs text-slate-500 font-semibold italic">
              Credit: Dennis Hansen, Zool. Mus. Zürich
            </p>
            {/* Dynamic CMS Gallery */}
            <Gallery slug="dinosaur-transport" columns={2} />
          </section>

          {/* Section 5: Specialtransport af gods */}
          <section className="space-y-4 border-t border-slate-200 pt-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#0f2b48]">
              Specialtransport af gods
            </h2>
            <p className="text-base text-slate-700 leading-relaxed">
              Fra tid til anden har vi arrangeret specialtransporter af forskelligt gods. Nedenstående er en af de mere eksotiske.
            </p>
            <div className="pt-2 max-w-xl">
              <button
                type="button"
                onClick={() => setSpecialLightboxIndex(0)}
                className="group relative aspect-16/10 overflow-hidden rounded-xl bg-slate-100 border border-slate-200 shadow-2xs hover:shadow-md transition-all text-left focus:outline-hidden cursor-pointer"
                aria-label="Vis specialtransport af dinosaur på nedbygget trailer"
              >
                <img
                  src={imageUrl(specialImages[0], 'width=900,quality=85,format=auto')}
                  alt="Fragt af Dinosaurus på nedbygget trailer"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                  <span className="opacity-0 group-hover:opacity-100 transition-opacity p-2.5 rounded-full bg-white/90 text-slate-900 shadow-md">
                    <i className="fa-solid fa-expand text-sm" aria-hidden="true" />
                  </span>
                </div>
              </button>
            </div>
            {specialLightboxIndex !== null && (
              <Lightbox
                images={specialImages}
                currentIndex={specialLightboxIndex}
                onClose={() => setSpecialLightboxIndex(null)}
                onNavigate={(newIdx) => setSpecialLightboxIndex(newIdx)}
              />
            )}
          </section>
        </div>
      </div>
    </>
  );
}
